import { periodToDate } from "../../components/Lit/acc-mixins/dates.js";

const removeLeadingZero = (numberString: string) => {
  const stringLength = numberString.length;
  if (stringLength > 0 && numberString[0] === "0") {
    return numberString.slice(1, stringLength);
  }

  return numberString;
};

/**
 * Returns accurate date object based on local time from period string to use for formatting with Intl.DateTimeFormat.
 *
 * @param period "202406"
 * @returns Date "Sat Jun 15 2024 00:00:00 GMT-0400 (Eastern Daylight Time) "
 */
export const getPeriodToDate = (period: string | number) => {
  const { year, month } = getMonthAndYear(period);
  return new Date(Number(year), Number(month) - 1, 15);
};

export const getMonthAndYear = (period: string | number): { year: string; month: string } => {
  const periodString = `${period}`;
  if (periodString.length !== 6) {
    return { year: "", month: "" };
  }
  const year = periodString.slice(0, 4);
  const month = periodString.slice(4, 6);
  const formattedMonth = removeLeadingZero(month);

  if (Number(formattedMonth) > 12) return { year: "", month: "" };

  return { year, month };
};

/**
 * Returns 4 digit PY (Performance Year)
 * @param period - The period string in either 'YYYY' or 'YYYYMM' format.
 * @returns ex: "2024"
 */
export const getPerformanceYear = (period: string | number): string => {
  const periodString = `${period}`;
  const year = periodString.slice(0, 4);
  const month = periodString.length === 6 ? periodString.slice(4, 6) : "01"; // Default to January if only year is provided

  if (Number(month) >= 9) {
    const performanceYear = Number(year) + 1;
    return performanceYear.toString();
  }
  return year;
};

/**
 * Returns 2 digit PY (Performance Year)
 * @param period - The period string in either 'YYYY' or 'YYYYMM' format.
 * @returns ex: "24"
 */
export const getPerformanceYearShort = (period: string | number): string => {
  const periodString = `${period}`;
  if (periodString.length === 4) {
    return getPerformanceYear(period).slice(2, 4);
  } else if (periodString.length === 6) {
    return getPerformanceYear(period).slice(2, 4);
  } else {
    throw new Error("Invalid period format. Expected 'YYYY' or 'YYYYMM'.");
  }
};

/**
 * Returns the performance year data for a given period, adjusted backward by a specified count.
 *
 * @param period "202405"
 * @param offsetYears The number of years to go back from the performance year. "1"
 * @returns selectedPerformanceYear: "2023", selectedPYStartPeriod: "202309"; selectedPYEndPeriod: "202408"
 */
export const getSelectedPerformanceYearData = (
  period: string,
  offsetYears = 0,
): { selectedPerformanceYear: string; selectedPYStartPeriod: string; selectedPYEndPeriod: string } => {
  const { year, month } = getMonthAndYear(period);
  let performanceYear = Number(year);

  if (Number(month) >= 9) {
    performanceYear += 1;
  }

  performanceYear -= offsetYears;

  return {
    selectedPerformanceYear: performanceYear.toString(),
    selectedPYStartPeriod: `${performanceYear - 1}09`,
    selectedPYEndPeriod: `${performanceYear}08`,
  };
};

/**
 * @returns "202406"
 */
export const getPeriodFromDate = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  return `${year}${month}`;
};

/**
 * Returns the current date as period
 * @returns "202406"
 */
export const getCurrentPeriod = () => {
  return getPeriodFromDate(new Date());
};

/**
 * @param period "202406"
 * @param locale "en-US"
 * @returns "Jun 2024"
 */
export const getShortPeriodFormat = (period: string, locale: string) => {
  const dateObject = getPeriodToDate(period);
  const shortDateFormatter = Intl.DateTimeFormat(locale, {
    month: "short",
    year: "numeric",
    calendar: "gregory",
  });
  const shortDate = shortDateFormatter.format(dateObject);
  return shortDate;
};

/**
 * @param period "202406"
 * @param locale "en-US"
 * @returns "Jun 24"
 */
export const getShortMonthYear = (period: string | number, locale: string) => {
  const dateObject = getPeriodToDate(period);
  const shortDateFormatter = Intl.DateTimeFormat(locale, {
    month: "short",
    year: "2-digit",
  });
  const shortDate = shortDateFormatter.format(dateObject);
  return shortDate;
};

/**
 * @param month 9
 * @param performanceYear 2026
 * @param locale "en-US"
 * @returns "September 25"
 */
export const getMonthLabel = (month: number, performanceYear: number, locale: string): string => {
  const calendarYear = month >= 9 ? performanceYear - 1 : performanceYear;
  return new Intl.DateTimeFormat(locale, { month: "long", year: "2-digit" }).format(
    new Date(calendarYear, month - 1, 1),
  );
};

/**
 * @param period "202408"
 * @returns "12"
 */
export const getFiscalMonthNumber = (period: string) => {
  // Extract the month part from YYYYMM
  const month = parseInt(period.slice(-2), 10);

  // Shift so that September (9) becomes 1, October (10) becomes 2, etc.
  // Months 9-12: subtract 8 (so 9→1, 10→2, 11→3, 12→4)
  // Months 1-8: add 4 (so 1→5, 2→6, ... 8→12)
  return month >= 9 ? month - 8 : month + 4;
};

/**
 * @param period "202406"
 * @param locale "en-US"
 * @returns "June 2024"
 */
export const getLongPeriodFormat = (period: string | number, locale: string) => {
  if (locale?.startsWith("ja")) {
    const { year, month } = getMonthAndYear(period);
    return `${year}年${month}月度`;
  }

  const dateObject = getPeriodToDate(period);
  const longDateFormatter = Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    calendar: "gregory",
  });
  const shortDate = longDateFormatter.format(dateObject);
  return shortDate;
};

/**
 * @param period "202406"
 * @param locale "ja-JP"
 * @returns "2024年"
 */
export const getYear = (period: string | number, locale: string) => {
  const dateObject = getPeriodToDate(period);
  const yearFormatter = Intl.DateTimeFormat(locale, {
    year: "numeric",
    calendar: "gregory",
  });
  const formatYear = yearFormatter.format(dateObject);
  return formatYear;
};

/**
 * @param period "202406"
 * @param locale "en-US"
 * @returns "June"
 */
export const getFullMonth = (period: string | number, locale: string) => {
  const dateObject = getPeriodToDate(period);
  const monthFormatter = Intl.DateTimeFormat(locale, {
    month: "long",
  });
  const formatMonth = monthFormatter.format(dateObject);
  return formatMonth;
};

/**
 * @param period "202406"
 * @param locale "en-US"
 * @returns "Jun"
 */
export const getShortMonth = (period: string | number, locale: string) => {
  const dateObject = getPeriodToDate(period);
  const monthFormatter = Intl.DateTimeFormat(locale, {
    month: "short",
  });
  const formatMonth = monthFormatter.format(dateObject);

  return formatMonth;
};

/**
 * @param period "202406"
 * @param locale "en-US"
 * @returns "J"
 */
export const getNarrowMonth = (period: string | number, locale: string) => {
  const dateObject = getPeriodToDate(period);
  const monthFormatter = Intl.DateTimeFormat(locale, {
    month: "narrow",
  });

  return monthFormatter.format(dateObject);
};

/**
 * Returns the Month of this AmwayPeriod as a zero-padded string. Eg: for August it returns '08'.
 * @param period
 * @returns "08"
 */
export const getMonthString = (period: string | number) => {
  const periodString = `${period}`;
  if (periodString.length === 6) {
    return periodString.slice(4);
  }
  return periodString;
};

const convertYearMonthToPeriod = (year: number, month: number) => {
  return month < 10 ? `${year}0${month}` : `${year}${month}`;
};

export const nextNPeriods = (period: string, n: number, inclusive: boolean) => {
  const periods = [];
  const { year, month } = getMonthAndYear(period);
  let currentYear = Number(year);
  let currentMonth = Number(month);
  let i = 0;
  if (inclusive) {
    periods.push(period.toString());
    i += 1;
  }

  for (; i < n; i += 1) {
    if (currentMonth === 12) {
      currentYear += 1;
      currentMonth = 1;
      periods.push(`${currentYear}01`);
    } else {
      currentMonth += 1;
      periods.push(convertYearMonthToPeriod(currentYear, currentMonth));
    }
  }

  return periods;
};

export const getPreviousNPeriods = (period: string, n: number, inclusive: boolean) => {
  const periods = [];
  const { year, month } = getMonthAndYear(period);
  let currentYear = Number(year);
  let currentMonth = Number(month);
  let i = 0;
  if (inclusive) {
    periods.push(period.toString());
    i += 1;
  }

  for (; i < n; i += 1) {
    if (currentMonth > 1) {
      currentMonth -= 1;
      periods.push(convertYearMonthToPeriod(currentYear, currentMonth));
    } else {
      currentYear -= 1;
      currentMonth = 12;
      periods.push(`${currentYear}${currentMonth}`);
    }
  }

  return periods;
};

/**
 * Returns an array of `n` number of the number representation of  AmwayPeriod(s) after this AmwayPeriod
 * If `inclusive` is `true`, the return array includes the string representation of this AmwayPeriod.
 *
 */
export const getNextNPeriodNumbers = (period: string, n: number, inclusive = false) => {
  const periods = nextNPeriods(period, n, inclusive);
  return periods.map((period) => Number(period));
};

export const numMonthsBetweenDates = (startPeriod: string, endPeriod: string) => {
  const startDate = periodToDate(startPeriod);
  const endDate = periodToDate(endPeriod);
  return endDate.getMonth() - startDate.getMonth() + 12 * (endDate.getFullYear() - startDate.getFullYear()) + 1;
};

export const getPrevYearShort = (period: string | number) => {
  const currentPYShort = getPerformanceYearShort(period);
  return parseInt(currentPYShort) - 1;
};

/**
 * Returns the 2 digit PY (Performance Year) following the given period.
 * @param period - The period string in either 'YYYY' or 'YYYYMM' format.
 * @returns ex: "27"
 */
export const getNextYearShort = (period: string | number): string => {
  const currentPYShort = getPerformanceYearShort(period);
  return (parseInt(currentPYShort) + 1).toString();
};

/**
 * Validates period format (YYYYMM)
 * @param period - Period string to validate
 * @returns boolean indicating if format is valid
 */
export const isValidPeriodFormat = (period: string): boolean => {
  if (typeof period !== "string" || period.length !== 6) {
    return false;
  }

  const year = period.slice(0, 4);
  const month = period.slice(4, 6);

  const yearNum = Number(year);
  const monthNum = Number(month);

  // Check if year and month are valid numbers
  if (isNaN(yearNum) || isNaN(monthNum)) {
    return false;
  }

  // Check if year is reasonable (between 2000 and 2100)
  if (yearNum < 2000 || yearNum > 2100) {
    return false;
  }

  // Check if month is valid (01-12)
  if (monthNum < 1 || monthNum > 12) {
    return false;
  }

  return true;
};

/**
 * Validates performance year format (YYYY)
 * @param performanceYear - Performance year string to validate
 * @returns boolean indicating if format is valid
 */
export const isValidPYFormat = (performanceYear: string): boolean => {
  if (typeof performanceYear !== "string" || performanceYear.length !== 4) {
    return false;
  }

  const yearNum = Number(performanceYear);

  // Check if year is a valid number
  if (isNaN(yearNum)) {
    return false;
  }

  // Check if year is slightly reasonable (between 1900 and 3000)
  if (yearNum < 1900 || yearNum > 3000) {
    return false;
  }

  return true;
};

/**
 * Calculate performance year from period (month >= 9 means next year)
 * @param period - Period in YYYYMM format
 * @returns Performance year in YYYY format
 */
export const calculatePerformanceYear = (period: string): string => {
  try {
    // Validate input format
    if (!isValidPeriodFormat(period)) {
      // Fallback to current year
      return new Date().getFullYear().toString();
    }

    // Use the existing getPerformanceYear function to avoid duplication
    return getPerformanceYear(period);
  } catch {
    return new Date().getFullYear().toString();
  }
};

/**
 * Get period from performance year with current PY vs historical PY logic
 * @param performanceYear - Performance year in YYYY format
 * @param currentPeriod - Current period in YYYYMM format
 * @returns Period in YYYYMM format
 */
export const getPeriodFromPY = (performanceYear: string, currentPeriod: string): string => {
  try {
    // Validate inputs
    if (!isValidPYFormat(performanceYear)) {
      return currentPeriod || getCurrentPeriod();
    }

    if (!isValidPeriodFormat(currentPeriod)) {
      currentPeriod = getCurrentPeriod();
    }

    const currentPY = calculatePerformanceYear(currentPeriod);

    // If selected PY matches current PY, use current period
    if (performanceYear === currentPY) {
      return currentPeriod;
    }

    // Otherwise, use last month of selected PY (August)
    return `${performanceYear}08`;
  } catch {
    return currentPeriod || getCurrentPeriod();
  }
};

/**
 * Get performance year from period using calculatePerformanceYear utility
 * @param period - Period in YYYYMM format
 * @returns Performance year in YYYY format
 */
export const getPYFromPeriod = (period: string): string => {
  try {
    // Validate input
    if (!isValidPeriodFormat(period)) {
      // Fallback to current period's PY
      return calculatePerformanceYear(getCurrentPeriod());
    }

    return calculatePerformanceYear(period);
  } catch {
    return calculatePerformanceYear(getCurrentPeriod());
  }
};
